import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Form, Input, Select } from 'antd';
import { toast } from 'react-toastify';

import { IoMdInformationCircle } from "react-icons/io";
import { MdOutlineKeyboardArrowLeft } from "react-icons/md";

import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';

import '../../../css/UserAdmin/Global.css';
import 'react-toastify/dist/ReactToastify.css';

const StrandForm = () => {
    const navigate = useNavigate();
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
    const [form] = Form.useForm();
    const variant = Form.useWatch('variant', form);
    const { id } = useParams();
    const [isArchived, setIsArchived] = useState(false);

    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchStrand = async () => {
            if (!id) return;

            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/strands/${id}`);
                const result = await res.json();

                if (!res.ok) {
                    return toast.error(result.message || 'Failed to load strand data.');
                }

                const data = result.data;
                setIsArchived(data.isArchived);

                form.setFieldsValue({
                    strand: data.strand,
                    strandName: data.strandName,
                    status: data.status,
                });

            } catch (error) {
                console.error('Error fetching strand:', error);
                toast.error('Error fetching strand data.');
            }
        };

        fetchStrand();
    }, [id, form]);

    const handleSubmit = async (values) => {
        try {
            // Trim and sanitize input
            const trimmedValues = {
                strand: values.strand.trim(),
                strandName: values.strandName.trim(),
                status: values.status.trim(),
            };

            // Determine if it's a create or update operation
            const url = id
                ? `${process.env.REACT_APP_API_URL}/api/admin/strands/${id}`
                : `${process.env.REACT_APP_API_URL}/api/admin/strands/create-strand`;

            const method = id ? 'PUT' : 'POST';

            // API call to create or update the strand
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(trimmedValues)
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 409) {
                    return toast.error(data.message);
                }
                if (response.status >= 400 && response.status < 500) {
                    return toast.warning(data.message || 'Invalid input.');
                }
                return toast.error('Server error. Please try again later.');
            }

            toast.success(id ? 'Strand updated successfully!' : 'Strand created successfully!');

            // Get values from localStorage without parsing as JSON
            const fullName = localStorage.getItem('fullName');
            const role = localStorage.getItem('role');
            const userID = localStorage.getItem('userID');

            // Log the action
            const logAction = id ? 'Update' : 'Create';
            const logDetail = `${logAction === 'Create' ? 'Created' : 'Updated'} strand [${trimmedValues.strandName}] with code ${trimmedValues.strand}`;

            const logData = {
                userID: userID,
                accountName: fullName,
                role: role,
                action: logAction,
                detail: logDetail,
            };

            // Make API call to save the system log
            fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-logs`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(logData)
            })
                .then(response => response.json())
                .then(data => {
                    console.log('System log recorded:', data);
                })
                .catch(error => {
                    console.error('Failed to record system log:', error);
                });

            navigate('/admin/manage-strands');

        } catch (error) {
            console.error('Error saving strand:', error);
            toast.error(error.message || 'Failed to save strand. Please try again.');
        }
    };


    const handleBack = () => navigate('/admin/manage-strands');

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                </div>
                <main className="admin-main-content-area">
                    <div className="page-title">
                        <div className="arrows" onClick={handleBack}>
                            <MdOutlineKeyboardArrowLeft />
                        </div>
                        <p className="heading">
                            {isArchived
                                ? 'View Archived Strand'  // Display when the account is archived
                                : id
                                    ? 'Edit Strand'           // Display when editing an existing account
                                    : 'Create Strand'         // Display when creating a new account
                            }
                        </p>
                    </div>

                    <Form
                        form={form}
                        variant={variant || 'outlined'}
                        initialValues={{ variant: 'outlined' }}
                        labelCol={{ xs: { span: 24 }, sm: { span: 6 } }}
                        wrapperCol={{ xs: { span: 24 }, sm: { span: 30 } }}
                        onFinish={handleSubmit}
                    >
                        <div className="container-columns">
                            <div className="column">
                                <div className="group-title">
                                    <IoMdInformationCircle className="section-icon" />
                                    <p className="section-title">STRAND DETAILS</p>
                                </div>
                                <Form.Item
                                    label="Strand"
                                    name="strand"
                                    rules={[{ required: true, message: 'Please input strand code!' }]}
                                >
                                    <Input placeholder="Please enter strand code" />
                                </Form.Item>
                                <Form.Item
                                    label="Strand Name"
                                    name="strandName"
                                    rules={[{ required: true, message: 'Please input strand name!' }]}
                                >
                                    <Input placeholder="Please enter strand name" />
                                </Form.Item>
                                <Form.Item
                                    label="Status"
                                    name="status"
                                    rules={[{ required: true, message: 'Please select a status!' }]}
                                >
                                    <Select placeholder="Select a status">
                                        <Select.Option value="Active">Active</Select.Option>
                                        <Select.Option value="Inactive">Inactive</Select.Option>
                                    </Select>
                                </Form.Item>
                                {!isArchived && (
                                    <div className="buttons" style={{ marginTop: '20px' }}>
                                        <Button type="default" htmlType="button" onClick={handleBack}>
                                            Cancel
                                        </Button>
                                        <Button type="primary" htmlType="submit" className={id ? '' : 'create-btn'}>
                                            {id ? 'Update' : 'Save'}
                                        </Button>
                                    </div>
                                )}
                            </div>

                            <div className="column">
                            </div>
                            <div className="column">
                            </div>
                        </div>
                    </Form>
                </main>
            </div>
            <Footer />
        </div>
    );
};

export default StrandForm;